/**
 * SquareLoader — STRONOM path loader (square stroke + travelling accent dot).
 *
 * Source: Uiverse "loader" by mobinkakei — `<rect x="8" y="8" width="64" height="64">`
 * with NO `rx`. Rounding comes from `stroke-linejoin: round` only. Path length must
 * stay exactly 256 (= dash period) so the 64-unit gap is one full edge and the dot
 * stays centred in it.
 *
 * Geometry / motion: docs/LOADING_STANDARD.md §1 and
 * docs/sandbox/loading-standard-sandbox.html.
 *
 * Direction: `animation-direction: reverse` on BOTH the rect and the `::before`
 * dot (variant C / clockwise). Do not flip by rewriting keyframes.
 *
 * Animated is the only normal form. Static outline exists solely inside
 * `@media (prefers-reduced-motion: reduce)` — there is no `variant` prop.
 *
 * Stroke: Review Console track idle cube icon white (`VIEW_CUBE_ICON_STROKE`).
 * Dot: `var(--accent-module)` (global.css — do not hardcode #38bdf8).
 * `tone="inherit"`: path + dot use `currentColor` (button / label chrome).
 *
 * No `motion` package. No shimmer / dots cluster / progress bar.
 */
import { cn } from "@/lib/utils";
import type { CSSProperties } from "react";

/**
 * Review Console track angle-chip idle icon stroke
 * (`ReviewConsoleTrack.angleIconColor` non-viewing / non-marked)
 * and `GeneratingBand` `LOADER_STROKE`.
 */
export const VIEW_CUBE_ICON_STROKE = "rgba(255,255,255,.72)";

export type SquareLoaderSize = 16 | 24 | 32 | 40;

export type SquareLoaderTone = "accent" | "inherit";

export type SquareLoaderProps = {
  /** Pixel edge length. Hub thumbs: 24; empty-grid query: 40; preview frame: 32. */
  size?: SquareLoaderSize | number;
  /**
   * `accent` (default): view-cube stroke + accent-module dot (content loaders).
   * `inherit`: path + dot use currentColor (button / chrome icons).
   */
  tone?: SquareLoaderTone;
  className?: string;
  /** Defaults to true — decorative; parent owns aria-busy. */
  "aria-hidden"?: boolean | "true" | "false";
};

export function SquareLoader({
  size = 24,
  tone = "accent",
  className,
  "aria-hidden": ariaHidden = true,
}: SquareLoaderProps) {
  const px = typeof size === "number" ? size : 24;
  const pathColor = tone === "inherit" ? "currentColor" : VIEW_CUBE_ICON_STROKE;
  const dotColor = tone === "inherit" ? "currentColor" : "var(--accent-module)";

  return (
    <span
      className={cn("sq-loader", className)}
      style={
        {
          ["--sz" as string]: `${px}px`,
          ["--path" as string]: pathColor,
          ["--dot" as string]: dotColor,
        } as CSSProperties
      }
      data-testid="square-loader"
      data-tone={tone}
      aria-hidden={ariaHidden}
    >
      <style>{`
        @keyframes sq-pathRect{
          25%{stroke-dashoffset:64;}
          50%{stroke-dashoffset:128;}
          75%{stroke-dashoffset:192;}
          100%{stroke-dashoffset:256;}
        }
        @keyframes sq-dotRect{
          25%{transform:translate(0,0);}
          50%{transform:translate(calc(var(--sz) * 18 / 44), calc(var(--sz) * -18 / 44));}
          75%{transform:translate(0, calc(var(--sz) * -36 / 44));}
          100%{transform:translate(calc(var(--sz) * -18 / 44), calc(var(--sz) * -18 / 44));}
        }
        .sq-loader{
          --duration:3s;
          --ease:cubic-bezier(.785,.135,.15,.86);
          width:var(--sz);
          height:var(--sz);
          position:relative;
          display:inline-block;
          flex-shrink:0;
        }
        .sq-loader::before{
          content:"";
          width:calc(var(--sz) * 6 / 44);
          height:calc(var(--sz) * 6 / 44);
          border-radius:50%;
          position:absolute;
          display:block;
          background:var(--dot);
          top:calc(var(--sz) * 37 / 44);
          left:calc(var(--sz) * 19 / 44);
          transform:translate(calc(var(--sz) * -18 / 44), calc(var(--sz) * -18 / 44));
          animation:sq-dotRect var(--duration) var(--ease) infinite;
          animation-direction:reverse;
        }
        .sq-loader svg{
          display:block;
          width:100%;
          height:100%;
        }
        .sq-loader svg rect{
          fill:none;
          stroke:var(--path);
          stroke-width:10;
          stroke-linejoin:round;
          stroke-linecap:round;
          stroke-dasharray:192 64 192 64;
          stroke-dashoffset:0;
          animation:sq-pathRect var(--duration) var(--ease) infinite;
          animation-direction:reverse;
        }
        @media (prefers-reduced-motion:reduce){
          .sq-loader::before,
          .sq-loader svg rect{
            animation:none !important;
          }
          .sq-loader svg rect{
            stroke-dasharray:none;
            opacity:.6;
          }
          .sq-loader::before{
            opacity:.6;
          }
        }
      `}</style>
      <svg viewBox="0 0 80 80" aria-hidden>
        <rect x="8" y="8" width="64" height="64" />
      </svg>
    </span>
  );
}

export default SquareLoader;
